/**
 * Centralized CSS custom properties for MuseIQ Lit components.
 * These mirror the :root variables in styles.css but can be imported
 * directly into component styles for type safety and IDE autocompletion.
 */
import { css } from 'lit';

/**
 * Theme CSS custom properties that can be applied to any component's :host.
 * Components inherit these from the document's :root, but this module
 * provides a reference and can be used for component-scoped overrides.
 */
export const theme = css`
    /* Colors - Core */
    --primary-color: #2563eb;
    --primary-dark: #1e40af;
    --primary-hover: #3b82f6;
    --secondary-color: #64748b;

    /* Status Colors */
    --success-color: #10b981;
    --warning-color: #f59e0b;
    --error-color: #ef4444;
    --danger-color: #dc2626;

    /* Surfaces */
    --background: #0f172a;
    --surface: #1e293b;
    --surface-light: #334155;

    /* Text */
    --text-primary: #f1f5f9;
    --text-secondary: #cbd5e1;
    --text-tertiary: #94a3b8;
    --text-muted: #64748b;

    /* Borders & Shadows */
    --border: #475569;
    --shadow: rgba(0, 0, 0, 0.5);
`;

/**
 * Color palette as TypeScript constants for programmatic access.
 * Use these when you need colors in JavaScript/TypeScript code.
 */
export const colors = {
    // Core
    primaryColor: '#2563eb',
    primaryDark: '#1e40af',
    primaryHover: '#3b82f6',
    secondaryColor: '#64748b',

    // Status
    successColor: '#10b981',
    warningColor: '#f59e0b',
    errorColor: '#ef4444',
    dangerColor: '#dc2626',

    // Surfaces
    background: '#0f172a',
    surface: '#1e293b',
    surfaceLight: '#334155',

    // Text
    textPrimary: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textTertiary: '#94a3b8',
    textMuted: '#64748b',

    // Borders & Shadows
    border: '#475569',
    shadow: 'rgba(0, 0, 0, 0.5)',
} as const;

export type ThemeColors = typeof colors;
